import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeIcons from '../../../../theme/Icons';
import ThemeContext from '../../../../theme/ThemeContext';
import Icon from '../../../misc/Icon';

const DropdownButton = styled.button`
  display: none;
  background: ${(props) => {
    const baseColor = props.theme.backgroundColor || 'rgba(0, 0, 0, 0.05)';
    return `${baseColor}80`;
  }};
  border: none;
  border-radius: ${(props) => {
    const radius = props.theme.borderRadius ? `${props.theme.borderRadius}px` : '0.3rem';
    return radius;
  }};
  cursor: pointer;
  margin-right: 5px !important;
  margin-left: 5px !important;
  padding: 0.25rem;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  margin-top: -3px !important;
  
  &:focus {
    outline: none;
  }
  
  svg {
    width: 12px;
    height: 12px;
    fill: ${(props) => props.theme.fontColorSecondary || 'black'};
  }
`;

const DropdownMenuContainer = styled.div`
  position: absolute;
  background-color: ${(props) => props.theme.backgroundColor || 'white'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e2e8f0'};
  border-radius: ${(props) => {
    const radius = props.theme.borderRadius ? `${props.theme.borderRadius}px` : '0.5rem';
    return radius;
  }};
  box-shadow: 0 4px 26px -1px rgba(0, 0, 0, 0.2);
  z-index: 30000;
  min-width: 180px;
  top: 100%;
  ${(props) => {
    if (props.leftAligned) {
      return 'left: 0;';
    }
    return 'right: 0;';
  }}
  margin-top: 5px;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${(props) => {
    if (props.isDelete) {
      return props.theme.dangerColor || '#e53e3e';
    }
    return props.theme.fontColorPrimary || '#4a5568';
  }};
  border-bottom: 1px solid ${(props) => props.theme.borderColor || '#e2e8f0'};
  z-index: 30001;
  
  &:hover {
    background-color: ${(props) => {
    if (props.isDelete) {
      const dangerBg = props.theme.dangerColor ? `${props.theme.dangerColor}10` : '#fff5f5';
      return dangerBg;
    }
    return props.theme.secondaryColor || '#f7fafc';
  }};
  }
  
  &:focus {
    outline: none;
    background-color: ${(props) => {
    if (props.isDelete) {
      const dangerBg = props.theme.dangerColor ? `${props.theme.dangerColor}10` : '#fff5f5';
      return dangerBg;
    }
    return props.theme.secondaryColor || '#f7fafc';
  }};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  ${(props) => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background-color: transparent;
    }
  `}
`;

const MenuItemWithSubmenu = styled(MenuItem)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &::after {
    content: ${(props) => {
    return props.leftAligned ? '"‹"' : '"›"';
  }};
    margin-left: 0.5rem;
  }
`;

const SubMenuContainer = styled.div`
  position: absolute;
  background-color: ${(props) => props.theme.backgroundColor || 'white'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e2e8f0'};
  border-radius: ${(props) => {
    const radius = props.theme.borderRadius ? `${props.theme.borderRadius}px` : '0.25rem';
    return radius;
  }};
  box-shadow: 0 4px 26px -1px rgba(0, 0, 0, 0.2);
  z-index: 30002;
  min-width: 180px;
  ${(props) => {
    if (props.leftAligned) {
      return 'left: 100%; right: auto;';
    }
    return 'right: 100%; left: auto;';
  }}
  top: 0;
  ${(props) => {
    if (props.leftAligned) {
      return 'margin-right: 5px;';
    }
    return 'margin-left: 5px;';
  }}
`;

/**
 * A reusable dropdown menu component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.triggerElement - Custom trigger element (optional)
 * @param {string} props.triggerIcon - Icon to use for the trigger button (optional)
 * @param {Object} props.buttonStyle - Custom styles for the trigger button
 * @param {Array} props.menuItems - Array of menu items
 * @param {Function} props.onClose - Function to call when the dropdown is closed
 * @param {string} props.iconColor - Color for the dropdown icon
 * @param {string} props.iconHoverColor - Hover color for the dropdown icon
 * @param {boolean} props.leftAligned - Whether to display the dropdown aligned to the left
 * @param {boolean} props.rightAligned - Whether to display the dropdown aligned to the right (default)
 * @param {boolean} props.isDescriptionColumn - Whether this is a description column
 * @param {string} props.label - Label for the column
 */
const DropdownMenu = ({
  triggerElement,
  triggerIcon = ThemeIcons.misc.chevronDown,
  buttonStyle,
  menuItems = [],
  onClose,
  leftAligned = false,
  isDescriptionColumn = false,
  isSummaryView = false,
  label = ''
}) => {
  const theme = useContext(ThemeContext);

  const [showDropdown, setShowDropdown] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const submenuRefs = useRef({});
  const submenuTimeouts = useRef({});

  const descriptionColumnWidth = 10 * label.length;

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(submenuTimeouts.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                               buttonRef.current && !buttonRef.current.contains(event.target);

      // Check if click is outside all submenus
      const isOutsideAllSubmenus = Object.entries(submenuRefs.current).every(([key, ref]) => {
        return !ref || !ref.contains(event.target) || key === activeSubmenu;
      });

      if (isOutsideDropdown && isOutsideAllSubmenus) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate the actual dropdown alignment based on props
  const dropdownLeftAligned = leftAligned;

  const closeDropdown = () => {
    setShowDropdown(false);
    setActiveSubmenu(null);
    if (onClose) onClose();
  };

  const handleDropdownToggle = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
    setActiveSubmenu(null);
  };

  const handleSubmenuMouseEnter = (e, itemId) => {
    e.stopPropagation();

    // Clear any existing timeout
    if (submenuTimeouts.current[itemId]) {
      clearTimeout(submenuTimeouts.current[itemId]);
      submenuTimeouts.current[itemId] = null;
    }

    setActiveSubmenu(itemId);
  };

  const handleSubmenuMouseLeave = (e, itemId) => {
    // Don't close immediately, give time to move to submenu
    submenuTimeouts.current[itemId] = setTimeout(() => {
      // Check if mouse is over the submenu before closing
      if (submenuRefs.current[itemId] && !submenuRefs.current[itemId].matches(':hover')) {
        setActiveSubmenu(null);
      }
    }, 300);
  };

  const handleSubmenuMouseEnterContainer = (itemId) => {
    // Clear any timeout when entering the submenu
    if (submenuTimeouts.current[itemId]) {
      clearTimeout(submenuTimeouts.current[itemId]);
      submenuTimeouts.current[itemId] = null;
    }
  };

  const handleMenuItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }

    if (!item.keepOpen) {
      closeDropdown();
    }
  };

  // Recursively render menu items
  const renderMenuItems = (items) => {
    return items.map((item, index) => {

      if (item.submenu) {
        return (
          <MenuItemWithSubmenu
            key={item._id || `submenu-${index}`}
            disabled={item.disabled}
            onMouseEnter={(e) => handleSubmenuMouseEnter(e, item._id || `submenu-${index}`)}
            onMouseLeave={(e) => handleSubmenuMouseLeave(e, item._id || `submenu-${index}`)}
            theme={theme}
            leftAligned={dropdownLeftAligned}
          >
            {item.label}

            {activeSubmenu === (item._id || `submenu-${index}`) && (
              <SubMenuContainer
                ref={(el) => submenuRefs.current[item._id || `submenu-${index}`] = el}
                onMouseEnter={() => handleSubmenuMouseEnterContainer(item._id || `submenu-${index}`)}
                onMouseLeave={() => setActiveSubmenu(null)}
                theme={theme}
                leftAligned={dropdownLeftAligned}
              >
                {renderMenuItems(item.submenu)}
              </SubMenuContainer>
            )}
          </MenuItemWithSubmenu>
        );
      }

      return (
        <MenuItem
          key={item._id || `item-${index}`}
          onClick={() => handleMenuItemClick(item)}
          disabled={item.disabled}
          isDelete={item.isDelete}
          theme={theme}
        >
          {item.label}
        </MenuItem>
      );
    });
  };

  return (
    <div
      style={{
        position: (() => {
          if (isSummaryView) {
            return 'relative';
          }
          return 'fixed';
        })(),
        zIndex: 30000,
        left: (() => {
          if (isDescriptionColumn) {
            return descriptionColumnWidth;
          }
          return 'inherit';
        })()
      }}
    >
      {(() => {
        if (triggerElement) {
          return (
            <div ref={buttonRef} onClick={handleDropdownToggle}>
              {triggerElement}
            </div>
          );
        }

        return (
          <DropdownButton
            ref={buttonRef}
            onClick={handleDropdownToggle}
            buttonStyle={buttonStyle}
            className="pricing-table-dropdown-button"
            theme={theme}
          >
            <Icon
              icon={triggerIcon}
              color={theme.fontColorPrimary}
            />
          </DropdownButton>
        );
      })()}

      {showDropdown && (
        <DropdownMenuContainer
          ref={dropdownRef}
          theme={theme}
          leftAligned={dropdownLeftAligned}
        >
          {renderMenuItems(menuItems)}
        </DropdownMenuContainer>
      )}
    </div>
  );
};

export default DropdownMenu;

DropdownMenu.propTypes = {
  triggerElement: PropTypes.node,
  triggerIcon: PropTypes.string,
  buttonStyle: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    submenu: PropTypes.array,
    isDelete: PropTypes.bool,
    disabled: PropTypes.bool
  })),
  onClose: PropTypes.func,
  leftAligned: PropTypes.bool,
  isDescriptionColumn: PropTypes.bool,
  isSummaryView: PropTypes.bool,
  label: PropTypes.string
};

